import { retrieveFirmSignatureRequestPublicV2 } from "@/lib/data/requests/firm/firm-signature-request";
import { retrieveMatterSignatureRequestPublicV2 } from "@/lib/data/requests/matters/matter-signature-request";
import type { LayoutField } from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";

export type SignaturePortalViewModel = {
  ownerId: string;
  dateModified: string;
  signatureRequestId: string;
  fileName: string;
  filePresignedUrlKey: string;
  expirationDate: string;
  fileVariables: string[];
  signatureRequestFields?: LayoutField[];
  initialDocUrl?: string;
  demoLayoutFields?: LayoutField[];
  requestingContact?: {
    label: string;
    dateModified: string;
    signatureRequestComplete?: boolean;
    pendingSigner?: boolean;
  };
  requestingUser?: {
    label: string;
    dateModified: string;
    signatureRequestComplete?: boolean;
    pendingSigner?: boolean;
  };
  contactConnections: {
    dateModified?: string;
    signatureRequestComplete?: boolean;
  }[];
  userConnections: {
    dateModified?: string;
    signatureRequestComplete?: boolean;
  }[];
  presignedUrlFileType:
    | "matter-signature-request-file"
    | "firm-signature-request-file"
    | "firm-file";
  demoMode?: boolean;
};

type MatterRetrieveData = Awaited<
  ReturnType<typeof retrieveMatterSignatureRequestPublicV2>
>["data"];

type FirmRetrieveData = Awaited<
  ReturnType<typeof retrieveFirmSignatureRequestPublicV2>
>["data"];

function isEmptyConnectionPayload(
  value: unknown,
): value is null | undefined | Record<string, never> {
  return (
    value == null ||
    (typeof value === "object" && Object.keys(value).length === 0)
  );
}

function sortSignatureRequestFields(fields: LayoutField[]) {
  return [...fields].sort((left, right) => {
    const leftOrder = left.signerOrder ?? Number.POSITIVE_INFINITY;
    const rightOrder = right.signerOrder ?? Number.POSITIVE_INFINITY;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    if (left.page !== right.page) {
      return left.page - right.page;
    }
    if (left.y !== right.y) {
      return left.y - right.y;
    }
    if (left.x !== right.x) {
      return left.x - right.x;
    }
    return left.label.localeCompare(right.label);
  });
}

function mapMatterRetrieveToViewModel(
  data: MatterRetrieveData,
): SignaturePortalViewModel {
  const mcRaw = data.requestingContactConnection;
  const muRaw = data.requestingUserConnection;
  const mc = isEmptyConnectionPayload(mcRaw) ? undefined : mcRaw;
  const mu = isEmptyConnectionPayload(muRaw) ? undefined : muRaw;

  return {
    ownerId: data.signatureRequest.matterId,
    dateModified: data.signatureRequest.dateModified,
    signatureRequestId: data.signatureRequest.id,
    fileName: data.signatureRequest.name,
    filePresignedUrlKey: data.signatureRequest.presignedUrlKey,
    expirationDate: data.signatureRequest.expirationDate,
    fileVariables: Array.isArray(data.signatureRequest.variables)
      ? data.signatureRequest.variables.filter(
          (value): value is string => typeof value === "string",
        )
      : [],
    signatureRequestFields: sortSignatureRequestFields(
      data.fields.map(
        (field) =>
          ({
            height: field.height,
            fontFamily: field.fontFamily ?? undefined,
            fontSize: field.fontSize ?? undefined,
            id: field.id,
            label: field.label,
            page: field.page,
            signerId: field.signerId,
            type: field.type,
            width: field.width,
            x: field.x,
            y: field.y,
          }) as LayoutField,
      ),
    ),
    initialDocUrl: undefined,
    demoLayoutFields: undefined,
    requestingContact: mc
      ? {
          label: mc.label ?? "Signer",
          dateModified: mc.dateModified ?? data.signatureRequest.dateModified,
          signatureRequestComplete: mc.signatureRequestComplete,
          pendingSigner: mc.pendingSigner,
        }
      : undefined,
    requestingUser: mu
      ? {
          label: mu.label ?? "Signer",
          dateModified: mu.dateModified ?? data.signatureRequest.dateModified,
          signatureRequestComplete: mu.signatureRequestComplete,
          pendingSigner: mu.pendingSigner,
        }
      : undefined,
    contactConnections: data.connections.map((c) => ({
      dateModified: c.dateModified,
      signatureRequestComplete: c.signatureRequestComplete ?? false,
    })),
    userConnections: data.userConnections.map((c) => ({
      dateModified: c.dateModified,
      signatureRequestComplete: c.signatureRequestComplete ?? false,
    })),
    presignedUrlFileType: "matter-signature-request-file",
    demoMode: false,
  };
}

function mapFirmRetrieveToViewModel(
  data: FirmRetrieveData,
): SignaturePortalViewModel {
  const fcRaw = data.requestingContactConnection;
  const fuRaw = data.requestingUserConnection;
  const fc = isEmptyConnectionPayload(fcRaw) ? undefined : fcRaw;
  const fu = isEmptyConnectionPayload(fuRaw) ? undefined : fuRaw;

  return {
    ownerId: data.signatureRequest.firmId,
    dateModified: data.signatureRequest.dateModified,
    signatureRequestId: data.signatureRequest.id,
    fileName: data.signatureRequest.name,
    filePresignedUrlKey: data.signatureRequest.presignedUrlKey,
    expirationDate: data.signatureRequest.expirationDate,
    fileVariables: Array.isArray(data.signatureRequest.variables)
      ? data.signatureRequest.variables.filter(
          (value): value is string => typeof value === "string",
        )
      : [],
    signatureRequestFields: sortSignatureRequestFields(
      data.fields.map(
        (field) =>
          ({
            height: field.height,
            fontFamily: field.fontFamily ?? undefined,
            fontSize: field.fontSize ?? undefined,
            id: field.id,
            label: field.label,
            page: field.page,
            signerId: field.signerId,
            type: field.type,
            width: field.width,
            x: field.x,
            y: field.y,
          }) as LayoutField,
      ),
    ),
    initialDocUrl: undefined,
    demoLayoutFields: undefined,
    requestingContact: fc
      ? {
          label: fc.label,
          dateModified: fc.dateModified,
          signatureRequestComplete: fc.signatureRequestComplete,
          pendingSigner: fc.pendingSigner,
        }
      : undefined,
    requestingUser: fu
      ? {
          label: fu.label,
          dateModified: fu.dateModified,
          signatureRequestComplete: fu.signatureRequestComplete,
          pendingSigner: fu.pendingSigner,
        }
      : undefined,
    contactConnections: data.contactConnections.map((c) => ({
      dateModified: c.dateModified,
      signatureRequestComplete: c.signatureRequestComplete,
    })),
    userConnections: data.userConnections.map((c) => ({
      dateModified: c.dateModified,
      signatureRequestComplete: c.signatureRequestComplete,
    })),
    presignedUrlFileType: "firm-signature-request-file",
    demoMode: false,
  };
}

export type PortalSignatureLoadResult =
  | {
      success: true;
      scope: "matter" | "firm";
      viewModel: SignaturePortalViewModel;
    }
  | { success: false };

/** Tries matter and firm retrieve in parallel; returns the first valid response. */
export async function loadPortalSignatureRequest(
  passcode: string,
): Promise<PortalSignatureLoadResult> {
  if (passcode.trim().toLowerCase() === "demo") {
    const response = await fetch("/signature-portal-demo.json");
    if (!response.ok) {
      return { success: false };
    }

    const fixture = (await response.json()) as SignaturePortalViewModel;
    return {
      success: true,
      scope:
        fixture.presignedUrlFileType !== "matter-signature-request-file"
          ? "firm"
          : "matter",
      viewModel: fixture,
    };
  }

  const [matterResult, firmResult] = await Promise.allSettled([
    retrieveMatterSignatureRequestPublicV2({
      signatureRequestPasscode: passcode,
    }),
    retrieveFirmSignatureRequestPublicV2({
      signatureRequestPasscode: passcode,
    }),
  ]);

  const matterApi =
    matterResult.status === "fulfilled" ? matterResult.value : null;
  const firmApi = firmResult.status === "fulfilled" ? firmResult.value : null;

  const matterFileName = matterApi?.data.signatureRequest.name;
  const matterValid =
    typeof matterFileName === "string" && matterFileName.length > 0;

  const firmFileName = firmApi?.data.signatureRequest.name;
  const firmValid = typeof firmFileName === "string" && firmFileName.length > 0;

  if (matterValid && matterApi) {
    return {
      success: true,
      scope: "matter",
      viewModel: mapMatterRetrieveToViewModel(matterApi.data),
    };
  }
  if (firmValid && firmApi) {
    return {
      success: true,
      scope: "firm",
      viewModel: mapFirmRetrieveToViewModel(firmApi.data),
    };
  }

  return { success: false };
}
